/**
 * `useLive(name, args)`: one live query, watched straight from r2db.
 *
 * The page asks this app's Worker for a lease on the named query (`/api/lease`), then opens r2db's
 * socket with it through `r2db/browser`. r2db sends one snapshot and then only what changed, and the
 * SDK folds that into rows already in the query's order — so a list, a board column or a thread is
 * current without the page ever re-reading it.
 *
 * **The whole page is one socket.** Every live query on it rides a single connection to the
 * database (`connect` from `r2db/browser`), so opening the board subscribes sixteen queries over
 * one upgrade and one engine connection in the object, not sixteen of each. It used to be one
 * socket per query: a page load spent 26 upgrades, each its own round trip, and the board filled
 * over about three seconds.
 *
 * Components that name the same query also share ONE subscription — the header's user picker and
 * the detail pane's owner menu are one lease, not two. A subscription outlives its last component
 * by a moment, so a remount (or React's development double-mount) does not mint a second lease.
 *
 * Changing `args` (a bigger window, another search) is a new query: until its snapshot arrives the
 * previous rows stay on screen, marked `stale`.
 */
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { connect } from 'r2db/browser';
import type { Lease, LiveResult, LiveStatus } from 'r2db/browser';

import { buildQuery, liveKey, QUERIES } from '../shared/queries.ts';
import type { QueryName } from '../shared/queries.ts';
import { schema } from '../shared/schema.ts';

/**
 * The lease the Worker minted into the document, if it is for the query being opened.
 *
 * The first paint's lease is the one round trip that cannot be overlapped by the page: the socket
 * URL arrives inside it, so the upgrade — and with it the object's wake — waits for it. The
 * Worker mints it while it is serving the HTML (`firstLease`), and this is where that is spent.
 *
 * Three guards, and each is a way the document can be older than it looks:
 *
 *   - **the key must match.** A visitor who landed on `/board`, or a `DEFAULT_PROJECT` that moved
 *     since the deploy, is a lease for a query nobody is opening.
 *   - **the TTL is measured on the page's OWN clock.** `performance.now()` is milliseconds since
 *     this document started loading, so it is an upper bound on the lease's age that needs no
 *     agreement with the Worker's wall clock — a browser whose clock is minutes off would make
 *     any absolute comparison wrong in whichever direction it is off.
 *   - **it is consumed once.** `lease` is called again on every reconnect, and a token that was
 *     valid when the page loaded is not a token to re-present an hour later.
 *
 * Anything that fails falls through to `/api/lease`, which is what the page did before this
 * existed — so the worst case is one ordinary lease, and there is no case where it is wrong.
 */
const MIN_LEASE_HEADROOM_MS = 10_000;
/**
 * How stale the DOCUMENT may be, by the wall clock, for its lease to be worth presenting.
 *
 * The second guard, and the one `performance.now()` cannot be: that clock measures this
 * navigation, so a document served from a cache looks brand new however old its token is. The
 * Worker marks such a document `no-store` for exactly this reason, and this is what holds if some
 * cache ignores it — a proxy, a service worker, a browser restoring a saved page.
 *
 * Compared against the Worker's clock, which is the one thing here that needs two machines to
 * agree, and the asymmetry is what makes that safe: a browser running fast reads the lease as old
 * and falls back, costing one ordinary `/api/lease`; a browser running slow reads a negative age,
 * which passes, and is then caught by the TTL check below. Neither can present a dead token.
 */
const MAX_DOCUMENT_AGE_MS = 20_000;
let inlined: { key: string; leaseToken: string; socketUrl: string; ttlMs: number; mintedAt?: number } | null =
  (globalThis as any).__R2DB_FIRST_LEASE ?? null;

function takeInlined(key: string): Lease | null {
  if (!inlined || inlined.key !== key) return null;
  const taken = inlined;
  inlined = null;
  if (taken.mintedAt !== undefined && Date.now() - taken.mintedAt > MAX_DOCUMENT_AGE_MS) return null;
  const remaining = taken.ttlMs - performance.now();
  if (remaining < MIN_LEASE_HEADROOM_MS) return null;
  return { leaseToken: taken.leaseToken, socketUrl: taken.socketUrl, expiresInMs: remaining } as Lease;
}

async function lease(name: QueryName, args: unknown, signal: AbortSignal): Promise<Lease> {
  const response = await fetch('/api/lease', {
    method: 'POST', signal,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name, args }),
  });
  const body: any = await response.json().catch(() => ({}));
  // The status rides along so the SDK can tell a retryable refusal (429, 503) from a final one.
  if (!response.ok) throw Object.assign(new Error(body.error ?? `Could not open a live query (${response.status}).`), { status: response.status });
  return body as Lease;
}

interface Snapshot { data: unknown; status: LiveStatus | 'loading'; error: Error | null }
interface Entry {
  snapshot: Snapshot;
  refs: number;
  listeners: Set<() => void>;
  close: () => void;
  linger?: ReturnType<typeof setTimeout>;
}
const LINGER_MS = 1500;
const entries = new Map<string, Entry>();

/**
 * The page's one connection to the database.
 *
 * Module scope, and never closed: a tab holds it for as long as it is open, and the socket is the
 * thing worth not re-paying for. An idle one costs nothing to keep — it hibernates, which by
 * design does NOT hold the object resident — while re-opening costs an upgrade, and a page between
 * views has zero live queries for a moment. `r2db/browser` re-opens lazily if that happens anyway.
 */
const live = connect({ schema });

// ── What this page is watching, for the counter that says so and the panel that lists it ────
const countWatchers = new Set<() => void>();
/** What one live query looks like from outside: enough to name it, show it and say how it is. */
export interface LiveQuery { key: string; name: QueryName; args: unknown; status: LiveStatus | 'loading'; rows: number | null; error: string | null }
let version = 0;
let listed: LiveQuery[] = [];
let listedVersion = -1;
const registryChanged = () => { version++; for (const w of countWatchers) w(); };
const watchRegistry = (cb: () => void) => { countWatchers.add(cb); return () => countWatchers.delete(cb); };
/**
 * The registry as a value, rebuilt only when something in it actually moved.
 *
 * `useSyncExternalStore` compares what `getSnapshot` returns by identity and re-renders when it
 * changes, so a fresh array on every call is an infinite loop. The version counter is what makes
 * the array stable between the changes that matter.
 */
function readRegistry(): LiveQuery[] {
  if (listedVersion !== version) {
    listed = [...entries].map(([key, entry]) => {
      const [name, args] = JSON.parse(key) as [QueryName, unknown];
      const data = entry.snapshot.data;
      return {
        key, name, args,
        status: entry.snapshot.status,
        rows: Array.isArray(data) ? data.length : data === undefined || data === null ? null : 1,
        error: entry.snapshot.error?.message ?? null,
      };
    });
    listedVersion = version;
  }
  return listed;
}
export function useLiveCount(): number {
  return useSyncExternalStore(watchRegistry, () => entries.size);
}
/** Every live query this page holds, newest last. Sorted by name so a board's five columns read
 *  as the five of one thing they are. */
export function useLiveQueries(): LiveQuery[] {
  const all = useSyncExternalStore(watchRegistry, readRegistry);
  return useMemo(() => [...all].sort((a, b) => a.name.localeCompare(b.name) || a.key.localeCompare(b.key)), [all]);
}

function acquire(key: string): Entry {
  let entry = entries.get(key);
  if (!entry) {
    const [name, args] = JSON.parse(key) as [QueryName, unknown];
    const controller = new AbortController();
    let result: LiveResult<unknown> | undefined;
    let detach: (() => void) | undefined;
    const created: Entry = {
      snapshot: { data: undefined, status: 'loading', error: null },
      refs: 0,
      listeners: new Set(),
      close: () => { controller.abort(); detach?.(); result?.close(); },
    };
    const publish = (snapshot: Snapshot) => { created.snapshot = snapshot; for (const l of created.listeners) l(); registryChanged(); };
    live.subscribe(buildQuery(name, args), { signal: controller.signal, lease: async signal => takeInlined(key) ?? lease(name, args, signal) })
      .then(live => {
        if (controller.signal.aborted) { live.close(); return; }
        result = live;
        // Fires now, then on every change to the rows or to the socket's status.
        detach = live.subscribe(data => publish({ data, status: live.status, error: live.error }));
      })
      .catch(error => { if (!controller.signal.aborted) publish({ ...created.snapshot, status: 'error', error }); });
    entries.set(key, created);
    registryChanged();
    entry = created;
  }
  clearTimeout(entry.linger);
  entry.refs++;
  return entry;
}

function release(key: string, entry: Entry) {
  if (--entry.refs > 0) return;
  entry.linger = setTimeout(() => {
    if (entry.refs > 0 || entries.get(key) !== entry) return;
    entries.delete(key);
    entry.close();
    registryChanged();
  }, LINGER_MS);
}

export interface Live<T> {
  data: T | undefined;
  status: LiveStatus | 'loading';
  error: Error | null;
  /** `data` belongs to the previous arguments; the new query's snapshot has not arrived yet. */
  stale: boolean;
}

/**
 * The arguments each query takes, read off `QUERIES` itself.
 *
 * `args` used to be `unknown`, which is how a required partition gets forgotten: every list and
 * board column must pin a `project` or the shape it builds streams the whole store, and the only
 * thing that noticed was the Worker's `parseArgs` at runtime. Derived from the query table, so a
 * query that gains an argument fails at every call site that does not pass it — which is where the
 * mistake is, rather than in a refusal a visitor sees.
 */
type ArgsOf<N extends QueryName> = Parameters<typeof QUERIES[N]>[0];
/**
 * One tuple per query: its name, and its arguments if it takes any.
 *
 * A union of tuples rather than a second type parameter, because a caller writes the ROW type
 * explicitly (`useLive<IssueCard[]>`) and TypeScript has no partial type-argument inference — a
 * second parameter would fall back to its default and check the arguments against the union of
 * every query's, which is how `useLive<User[]>('users')` and a `project`-less `'issues'` both
 * passed. Spread as rest parameters, so each name admits exactly its own arguments.
 */
type LiveCall = {
  [N in QueryName]: ArgsOf<N> extends undefined ? [name: N] : [name: N, args: ArgsOf<N>];
}[QueryName];

export function useLive<T>(...call: LiveCall): Live<T> {
  const [name, args] = call;
  const key = liveKey(name, args);
  const [, rerender] = useState(0);
  const entryRef = useRef<{ key: string; entry: Entry } | null>(null);
  const last = useRef<{ key: string; data: unknown } | null>(null);

  useEffect(() => {
    const entry = acquire(key);
    entryRef.current = { key, entry };
    const listener = () => rerender(n => n + 1);
    entry.listeners.add(listener);
    listener();
    return () => { entry.listeners.delete(listener); release(key, entry); };
  }, [key]);

  const current = entryRef.current?.key === key ? entryRef.current.entry.snapshot : null;
  if (current && current.data !== undefined) last.current = { key, data: current.data };
  const fresh = current !== null && current.data !== undefined;
  return {
    data: (fresh ? current!.data : last.current?.data) as T | undefined,
    status: current?.status ?? 'loading',
    error: current?.error ?? null,
    stale: !fresh && last.current !== null && last.current.key !== key,
  };
}
